/**
 * Brandmark hover animation controller.
 *
 * The looping animation lives inside the inlined brandmark SVG (see
 * `resources/images/icons/skill-brandmark-hover.svg`). CSS alone can only start
 * and stop it on `:hover`, which cuts the loop off mid-cycle when the pointer
 * leaves. This controller keeps the loop running past `mouseleave` until the
 * current cycle finishes, so the animation always ends on a complete pass.
 *
 * The cycle is a round trip — the diamonds swap places and swap back, which is
 * what lets the last frame land exactly on the resting logo — so a wind-down
 * can take up to one full cycle (6.6s) after the pointer leaves.
 */

const PLAYING_CLASS = 'is-playing';
const INTERACTIVE_CLASS = 'is-interactive';

// One of the orbiting groups, all of which share a 0s delay and the same
// duration — its iteration boundary is the boundary of the whole composition.
const CYCLE_REFERENCE_SELECTOR = 'g.o0';
const CYCLE_ANIMATION_NAME = 'sk-o0';

const setupBrandmark = (brandmark: SVGSVGElement): void => {
  const trigger = brandmark.closest('a') ?? brandmark;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Opting out of the CSS-only `:hover` fallback baked into the SVG.
  brandmark.classList.add(INTERACTIVE_CLASS);

  let hovered = false;
  let stopPending = false;

  const stop = (): void => {
    stopPending = false;
    brandmark.classList.remove(PLAYING_CLASS);
  };

  brandmark.addEventListener('animationiteration', (event: AnimationEvent) => {
    // Every animated group fires this; only the reference group marks a cycle end.
    if (event.animationName !== CYCLE_ANIMATION_NAME) return;
    if (!stopPending || hovered) return;

    stop();
  });

  trigger.addEventListener('mouseenter', () => {
    hovered = true;

    // Re-entering during the wind-down keeps the existing loop going rather
    // than restarting it, so the motion stays continuous.
    stopPending = false;

    if (reducedMotion.matches) return;

    brandmark.classList.add(PLAYING_CLASS);
  });

  trigger.addEventListener('mouseleave', () => {
    hovered = false;

    if (!brandmark.classList.contains(PLAYING_CLASS)) return;

    // Reduced motion hides the animated layer entirely, so no iteration event
    // will ever arrive to end the loop for us.
    if (reducedMotion.matches || !brandmark.querySelector(CYCLE_REFERENCE_SELECTOR)) {
      stop();
      return;
    }

    stopPending = true;
  });
};

const BrandmarkHover = (): void => {
  const brandmarks = document.querySelectorAll<SVGSVGElement>('svg.a-skill-brandmark');

  brandmarks.forEach(setupBrandmark);
};

export default BrandmarkHover;
